import React, { useState } from 'react';
import axios from 'axios';
import { useSpring, animated } from '@react-spring/web';
import API_BASE_URL from '../../../ApiConfig/ApiConfig';
import Cookies from "js-cookie";

const TicketStatus = () => {
  const [ticketNumber, setTicketNumber] = useState('');
  const [statusUpdates, setStatusUpdates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);
  const token = Cookies.get("token");

  const animationProps = useSpring({
    opacity: statusUpdates.length > 0 ? 1 : 0,
    transform: statusUpdates.length > 0 ? 'translateY(0)' : 'translateY(-20px)',
    config: { duration: 300 },
  });

  const dotAnimation = useSpring({
    from: { transform: 'translateY(-20px)' },
    to: { transform: 'translateY(0)' },
    config: { duration: 500 },
    reset: true,
  });

  const handleInputChange = (e) => {
    setTicketNumber(e.target.value);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    };
    return new Intl.DateTimeFormat('en-GB', options).format(date);
  };

  const handleSearch = async () => {
    if (!ticketNumber) {
      setError('Please enter a ticket number.');
      return;
    }

    setLoading(true);
    setError('');
    setSearched(true);

    try {
      const response = await axios.get(`${API_BASE_URL}/TicketSearch/Search/${ticketNumber}`,
        { 
          headers: { 
            "Content-Type": "application/json",
            "Authorization": token ? `Bearer ${token}` : "",
          } 
        });
      if (response.data.statusUpdates) {
        setStatusUpdates(response.data.statusUpdates);
      } else {
        setError('Ticket not found. Please verify the ticket number.');
      }
    } catch (err) {
      setError('An error occurred while fetching the ticket status. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const makeLinksClickable = (text) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.split(urlRegex).map((part, index) => {
      if (urlRegex.test(part)) {
        return (
          <a 
            key={index} 
            href={part} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="text-blue-600 hover:text-blue-800 hover:underline transition-colors duration-200"
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-blue-800 p-6 text-white">
          <h1 className="text-2xl font-bold">SecureNet Support</h1>
          <p className="text-blue-100">Track your ticket status</p>
        </div>
        
        {/* Content */}
        <div className="p-6">
          {!searched ? (
            <div className="space-y-6">
              <div>
                <label htmlFor="ticketNumber" className="block text-sm font-medium text-gray-700 mb-1">
                  Ticket Reference Number
                </label>
                <input
                  type="text"
                  id="ticketNumber"
                  value={ticketNumber}
                  onChange={handleInputChange}
                  placeholder="Enter your ticket number"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                />
                {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
              </div>
              
              <button
                onClick={handleSearch}
                className="w-full bg-blue-700 hover:bg-blue-800 text-white font-medium py-3 px-4 rounded-lg shadow-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Track Status
              </button>
            </div>
          ) : (
            <div>
              <button 
                onClick={() => setSearched(false)}
                className="mb-6 flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-200"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
                Back to search
              </button>

              {loading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : error ? (
                <div className="bg-red-50 border-l-4 border-red-500 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                </div>
              ) : statusUpdates.length > 0 ? (
                <animated.div style={{ ...animationProps }}>
                  <div className="mb-6">
                    <h2 className="text-lg font-semibold text-gray-800">Ticket #{ticketNumber}</h2>
                    <p className="text-sm text-gray-500">Last updated: {formatDate(statusUpdates[statusUpdates.length - 1].date)}</p>
                  </div>

                  <div className="relative">
                    <div className="absolute left-4 top-0 h-full w-0.5 bg-blue-200"></div>
                    <ul className="space-y-8">
                      {statusUpdates.map((update, index) => (
                        <animated.li
                          key={index}
                          className="relative pl-10"
                          style={dotAnimation}
                        >
                          <div className={`absolute left-0 top-0 w-8 h-8 rounded-full flex items-center justify-center 
                            ${index === statusUpdates.length - 1 ? 'bg-blue-600' : 'bg-blue-200'}`}>
                            {index === statusUpdates.length - 1 ? (
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            ) : (
                              <span className="text-xs font-semibold text-blue-800">{index + 1}</span>
                            )}
                          </div>
                          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <div className="flex justify-between items-start">
                              <h3 className="font-medium text-gray-900">{makeLinksClickable(update.status)}</h3>
                              <span className="text-sm text-gray-500 whitespace-nowrap ml-4">{formatDate(update.date)}</span>
                            </div>
                          </div>
                        </animated.li>
                      ))}
                    </ul>
                  </div>
                </animated.div>
              ) : null}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            Need further assistance? Contact our support team at support@securenet.com
          </p>
        </div>
      </div>
    </div>
  );
};

export default TicketStatus;